import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { doc, getDoc, setDoc, query, collection, getDocs, updateDoc, where } from 'firebase/firestore';
import { auth, db } from '../config/firebase';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [userCompany, setUserCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  // Sign up function
  const signup = async (email, password, userData) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update user profile
      await updateProfile(user, {
        displayName: userData.displayName || userData.name
      });

      // Save additional user data to Firestore
      await setDoc(doc(db, 'users', user.uid), {
        ...userData,
        email: user.email,
        createdAt: new Date(),
        updatedAt: new Date()
      });

      return user;
    } catch (error) {
      throw error;
    }
  };

  // Login function
  const login = async (email, password) => {
    try {
      console.log('=== LOGIN ATTEMPT START ===');
      console.log('Attempting login for:', email);
      console.log('Password provided:', password ? 'Yes' : 'No');
      
      // First try to sign in normally
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password);
        console.log('Login successful for existing user:', userCredential.user.uid);
        return userCredential.user;
      } catch (authError) {
        console.log('Standard login failed:', authError.code);
        console.log('Auth error message:', authError.message);
        
        // If user doesn't exist, check if it's a tenant
        if (authError.code === 'auth/user-not-found') {
          console.log('User not found in Firebase Auth, checking if tenant exists...');
          
          // Check if this email exists in our users collection
          const usersQuery = query(collection(db, 'users'), where('email', '==', email));
          const usersSnapshot = await getDocs(usersQuery);
          
          if (!usersSnapshot.empty) {
            const userDoc = usersSnapshot.docs[0];
            const userData = userDoc.data();
            
            console.log('Found user document:', userData);
            
            // If it's a tenant and they're using their email as password, create Firebase Auth user
            if (userData.role === 'tenant' && password === email) {
              console.log('Creating Firebase Auth user for tenant...');
              
              try {
                const newUserCredential = await createUserWithEmailAndPassword(auth, email, password);
                console.log('Created Firebase Auth user for tenant:', newUserCredential.user.uid);
                
                // Update the user document with the Firebase Auth UID
                await updateDoc(doc(db, 'users', userDoc.id), {
                  uid: newUserCredential.user.uid,
                  firebaseAuthUid: newUserCredential.user.uid,
                  updatedAt: new Date()
                });
                
                console.log('Updated user document with Firebase Auth UID');
                return newUserCredential.user;
              } catch (createError) {
                console.error('Error creating Firebase Auth user for tenant:', createError);
                throw new Error('Failed to create tenant account. Please contact your property manager.');
              }
            }
          } else {
            // No user document found, check if it's a tenant in the tenants collection
            console.log('No user document found, checking tenants collection...');
            
            // Import tenantService here to avoid circular dependency
            const { tenantService } = await import('../services/tenantService');
            
            // Get all tenants and find by email
            const tenantsQuery = query(collection(db, 'tenants'), where('email', '==', email));
            const tenantsSnapshot = await getDocs(tenantsQuery);
            
            if (!tenantsSnapshot.empty) {
              const tenantDoc = tenantsSnapshot.docs[0];
              const tenantData = tenantDoc.data();
              
              console.log('Found tenant document:', tenantData);
              console.log('Tenant document ID:', tenantDoc.id);
              
              // If it's a tenant and they're using their email as password, create Firebase Auth user
              if (password === email) {
                console.log('Creating Firebase Auth user for new tenant...');
                
                try {
                  const newUserCredential = await createUserWithEmailAndPassword(auth, email, password);
                  console.log('Created Firebase Auth user for tenant:', newUserCredential.user.uid);
                  
                  // Create user document with Firebase Auth UID as document ID
                  const userDocData = {
                    name: tenantData.name,
                    email: tenantData.email,
                    role: 'tenant',
                    companyId: tenantData.companyId,
                    status: 'active',
                    createdAt: new Date(),
                    updatedAt: new Date(),
                    isTemporaryPassword: true,
                    uid: newUserCredential.user.uid
                  };
                  
                  console.log('Creating user document with data:', userDocData);
                  await setDoc(doc(db, 'users', newUserCredential.user.uid), userDocData);
                  console.log('Created user document for tenant');
                  
                  // Update tenant document with Firebase Auth UID
                  await updateDoc(doc(db, 'tenants', tenantDoc.id), {
                    authUid: newUserCredential.user.uid,
                    updatedAt: new Date()
                  });
                  console.log('Updated tenant document with Firebase Auth UID');
                  
                  return newUserCredential.user;
                } catch (createError) {
                  console.error('Error creating Firebase Auth user for tenant:', createError);
                  console.error('Create error code:', createError.code);
                  console.error('Create error message:', createError.message);
                  throw new Error('Failed to create tenant account. Please contact your property manager.');
                }
              } else {
                console.log('Password does not match email for tenant');
                throw new Error('Invalid password. Please use your email address as the password.');
              }
            } else {
              console.log('No tenant document found for email:', email);
            }
          }
        }
        
        // If we get here, it's a normal login error
        console.log('Throwing original auth error:', authError);
        throw authError;
      }
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  // Fetch user data from Firestore
  const fetchUserData = async (uid) => {
    try {
      console.log('=== FETCH USER DATA START ===');
      console.log('Fetching user data for UID:', uid);
      console.log('Current user email:', auth.currentUser?.email);
      
      // For tenants, we need to look up by email since the Firebase Auth UID 
      // might not match the user document ID
      if (auth.currentUser?.email) {
        console.log('Looking up user by email:', auth.currentUser.email);
        const usersQuery = query(collection(db, 'users'), where('email', '==', auth.currentUser.email));
        const usersSnapshot = await getDocs(usersQuery);
        
        if (!usersSnapshot.empty) {
          const userDoc = usersSnapshot.docs[0];
          const userData = userDoc.data();
          console.log('Found user by email:', userData);
          console.log('User role found:', userData.role);
          console.log('User company found:', userData.companyId);
          
          // Check if this user should be a tenant but has wrong role
          if (userData.role === 'admin' || userData.role === 'superadmin') {
            console.log('User has admin role, checking if they should be a tenant...');
            const tenantsQuery = query(collection(db, 'tenants'), where('email', '==', auth.currentUser.email));
            const tenantsSnapshot = await getDocs(tenantsQuery);
            
            if (!tenantsSnapshot.empty) {
              console.log('Found tenant document for admin user - this is a security issue!');
              console.log('Tenant data:', tenantsSnapshot.docs[0].data());
              
              // Fix the user role immediately
              console.log('Fixing user role from admin to tenant...');
              await updateDoc(doc(db, 'users', userDoc.id), {
                role: 'tenant',
                updatedAt: new Date()
              });
              
              // Update the user data
              userData.role = 'tenant';
              console.log('User role fixed to tenant');
            }
          }
          
          console.log('Setting user role to:', userData.role);
          console.log('Setting user company to:', userData.companyId);
          setUserRole(userData.role);
          setUserCompany(userData.companyId);
          return userData;
        }
      }
      
      // Fallback: try to get user document by UID as document ID
      const userDoc = await getDoc(doc(db, 'users', uid));
      console.log('User document exists by UID:', userDoc.exists());
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        console.log('User data found by UID:', userData);
        console.log('User role found:', userData.role);
        console.log('User company found:', userData.companyId);
        
        // Check if this user should be a tenant but has wrong role
        if (userData.role === 'admin' || userData.role === 'superadmin') {
          console.log('User has admin role, checking if they should be a tenant...');
          const tenantsQuery = query(collection(db, 'tenants'), where('email', '==', auth.currentUser.email));
          const tenantsSnapshot = await getDocs(tenantsQuery);
          
          if (!tenantsSnapshot.empty) {
            console.log('Found tenant document for admin user - this is a security issue!');
            console.log('Tenant data:', tenantsSnapshot.docs[0].data());
            
            // Fix the user role immediately
            console.log('Fixing user role from admin to tenant...');
            await updateDoc(doc(db, 'users', uid), {
              role: 'tenant',
              updatedAt: new Date()
            });
            
            // Update the user data
            userData.role = 'tenant';
            console.log('User role fixed to tenant');
          }
        }
        
        console.log('Setting user role to:', userData.role);
        console.log('Setting user company to:', userData.companyId);
        setUserRole(userData.role);
        setUserCompany(userData.companyId);
        return userData;
      } else {
        console.log('User document does not exist for UID:', uid);
        console.log('Current user metadata:', auth.currentUser?.metadata);
        
        // User document doesn't exist - this should not happen for tenants
        // Only create admin user for existing Firebase Auth users that don't have a document
        // This is mainly for backward compatibility with existing admin users
        
        // Before creating admin user, check if this is a tenant
        if (auth.currentUser?.email) {
          console.log('Checking if user is a tenant before creating admin fallback...');
          const tenantsQuery = query(collection(db, 'tenants'), where('email', '==', auth.currentUser.email));
          const tenantsSnapshot = await getDocs(tenantsQuery);
          
          if (!tenantsSnapshot.empty) {
            console.log('Found tenant document, should not create admin user');
            throw new Error('Tenant user document not found. Please contact your property manager.');
          }
        }
        
        const userData = {
          name: auth.currentUser?.displayName || 'System Administrator',
          email: auth.currentUser?.email,
          role: 'admin', // Default to admin for existing users
          status: 'active',
          createdAt: new Date(),
          updatedAt: new Date(),
          permissions: ['manage_companies', 'manage_users', 'view_all_properties'],
          isSystemAdmin: true
        };

        console.log('Creating fallback admin user with data:', userData);
        await setDoc(doc(db, 'users', uid), userData);
        console.log('Setting user role to admin (fallback)');
        setUserRole(userData.role);
        setUserCompany(userData.companyId);
        return userData;
      }
    } catch (error) {
      console.error('Error in fetchUserData:', error);
      // Set default role as fallback
      setUserRole('admin');
      return null;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('=== AUTH STATE CHANGED ===');
      console.log('Previous user:', currentUser?.uid);
      console.log('New user:', user?.uid);
      console.log('Previous role:', userRole);
      
      if (user) {
        setCurrentUser(user);
        console.log('Fetching user data for:', user.uid);
        await fetchUserData(user.uid);
        console.log('User role after fetch:', userRole);
      } else {
        console.log('User logged out, clearing state');
        setCurrentUser(null);
        setUserRole(null);
        setUserCompany(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    userCompany,
    login,
    signup,
    logout,
    fetchUserData
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 